import {observer} from "mobx-react";
import styled from "styled-components";
import {Swiper, SwiperSlide} from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import SelectBox from "@/components/common/SelectBox";
import {useStore} from "@/store";
import {SIZES} from "@/components/constants";
import { useEffect, useRef, useState} from "react";
import {useRouter, useSearchParams} from "next/navigation";
import {GrFormNext, GrFormPrevious} from "react-icons/gr";
import {useMediaQuery} from "react-responsive";
import Link from "next/link";

const PageDepthsInformation = observer(({scrollRef}) => {
    const {navigationStore} = useStore();
    const isTablet = useMediaQuery({
        query: `(max-width: ${SIZES.TABLET})`
    });
    const depthRef = useRef(null);
    const swiperRef = useRef(null);
    const [showArrow, setShowArrow] = useState(false);
    const params = useSearchParams();
    const router = useRouter();
    const menuId = params.get("menu-id");
    useEffect(() => {
        if (!scrollRef?.current || !menuId) return;
        const container = scrollRef.current;
        const handleScroll = () => {
            if (!depthRef.current) return;
            const rect = depthRef.current.getBoundingClientRect();
            const headerHeight = !isTablet ? 96 : 64;
            if (rect.top <= headerHeight) {
                document.body.classList.add("depth-fixed");
            } else {
                document.body.classList.remove("depth-fixed");
            }
        };
        handleScroll();
        container.addEventListener("scroll", handleScroll);
        window.addEventListener("resize", handleScroll);
        return () => {
            container.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
            document.body.classList.remove("depth-fixed");
        };
    }, [scrollRef, isTablet, menuId]);


    const updateArrowState = (swiper) => {
        if (!swiper) return;
        setShowArrow(!swiper.isLocked);
    };
    const prevRef = useRef(null);
    const nextRef = useRef(null);

    return(
        <>
            {
                menuId &&
                navigationStore.setMakePageMenu(menuId) && navigationStore.setMakePageMenu(menuId).length > 0 &&
                <PageDepthsInfoWrap>
                    <div
                        ref={depthRef}
                        className={`depth-list-wrap `}
                    >
                        <div className="menu">
                            <div
                                ref={prevRef}
                                className={`depth-prev ${showArrow ? "active" : ""}`}
                            >
                                <GrFormPrevious/>
                            </div>
                            <Swiper
                                modules={[Navigation]}
                                observer={true}
                                observeParents={true}
                                spaceBetween={8}
                                slidesPerView="auto"
                                navigation={{
                                    prevEl: ".depth-prev",
                                    nextEl: ".depth-next",
                                }}
                                touchStartPreventDefault={false}
                                preventClicks={false}
                                preventClicksPropagation={false}
                                noSwiping={true}
                                noSwipingClass="no-swiping"
                                onInit={(swiper) => {
                                    swiperRef.current = swiper;
                                    swiper.navigation.destroy();
                                    swiper.navigation.init();
                                    swiper.navigation.update();

                                    updateArrowState(swiper);
                                }}

                                onResize={(swiper) => {
                                    updateArrowState(swiper);
                                    swiper.navigation.update();
                                }}

                                onObserverUpdate={(swiper) => {
                                    updateArrowState(swiper);
                                    swiper.navigation.update();
                                }}
                                initialSlide={Math.max(
                                    0,
                                    navigationStore.setMakePageMenu(menuId).findIndex(
                                        (item) => item.active || item.selectMenu?.active
                                    )
                                )}
                            >

                                {navigationStore.setMakePageMenu(menuId).map((item, idx) => (
                                    <SwiperSlide
                                        key={idx}
                                        className={`sub-list ${item.active ? "active" : ""} ${
                                            item.selectMenu ? "sub-list-select" : ""
                                        } ${item.selectMenu?.active ? "active" : ""}`}
                                    >
                                        {item.selectMenu ? (
                                            <div
                                                className="no-swiping"
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onTouchStart={(e) => e.stopPropagation()}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <SelectBox
                                                    option={item.selectMenu.option}
                                                    type="menu"
                                                    placeholder={
                                                        item.selectMenu.value
                                                            ? item.selectMenu.value
                                                            : item.selectMenu.title
                                                    }
                                                    arrow={false}
                                                    round={true}
                                                    value={item.selectMenu.value}
                                                    onChange={async (e) => {
                                                        const target = item.selectMenu.data.find(
                                                            (node) => node.menuId === e.value
                                                        );
                                                        const pagePath  = navigationStore.setFilterPageUrl(target, undefined, menuId);
                                                        router.push(pagePath);
                                                        requestAnimationFrame(() => {
                                                            if (swiperRef.current) {
                                                                swiperRef.current.updateSlides();
                                                                swiperRef.current.updateSize();
                                                                swiperRef.current.update();
                                                            }
                                                        });
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <Link
                                                className={"sub-list-inner"}
                                                href={navigationStore.setFilterPageUrl(item, undefined, menuId)}
                                            >
                                                {item.title}
                                            </Link>
                                        )}
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                            <div
                                ref={nextRef}
                                className={`depth-next ${showArrow ? "active" : ""}`}
                            >
                                <GrFormNext/>
                            </div>

                        </div>
                    </div>
                </PageDepthsInfoWrap>
            }
        </>
    )
});
const PageDepthsInfoWrap = styled.div`
    position: sticky;
    top: 0;
    left: 0;
    z-index: 35;

    .depth-list-wrap {
        padding: 24px 0;
        width: 100%;
        background-color: #F3F2F9;

        &.fixed {
            position: fixed;
            left: 0;
            right: 0;
            z-index: 15;
            top: 96px;
        }

        .menu {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 16px 35px;
            gap: 20px;
            position: relative;


            .depth-prev, .depth-next{
                cursor: pointer;
                flex-shrink: 0;
                width: 35px;
                height: 35px;
                background-color: #fff;
                border-radius: 50%;
                display: none;
                align-items: center;
                justify-content: center;
                svg{
                    width: 20px;
                    height: 20px;
                }
                &.active{
                    display: flex;
                }
            }

            .sub-list {
                width: auto;

                .sub-list-inner {
                    display: flex;
                    padding: 16px 24px;
                    border-radius: 8px;
                    font-weight: 600;
                    font-size: 18px;
                    line-height: 24px;
                    letter-spacing: 0.02em;
                    color: #23203B;
                    border: 1px solid #CCCCCC;
                    background: #fff;
                    box-sizing: border-box;
                    cursor: pointer;
                }

                &.active {
                    .sub-list-inner {
                        background: #4939B1;
                        color: #FFFFFF;
                        border-color: #4939B1;
                    }
                }

                &.sub-list-select {
                    padding: 0;
                    border: none;
                }
            }

            .sub-list-select {
                .react-select-container {
                    .react-select__control {
                        padding: 0 24px;
                        height: 58px;
                        font-size: 18px;
                        font-weight: 600;
                        background-color: #fff;
                        letter-spacing: 0.02em;
                        color: #23203B;
                        border: 1px solid #CCCCCC;
                        border-radius: 8px;
                    }
                }

                .react-select__menu {
                    width: 100%;
                }

                .react-select__placeholder {
                    color: #23203B;
                }

                .react-select__option {
                    width: 100%;
                    word-break: keep-all;
                    white-space: nowrap;
                }

                .react-select__option:hover {
                    background-color: #4939B1;
                    color: #fff;
                }

                &.active {
                    .react-select-container {
                        .react-select__control {
                            background-color: #4939B1;
                            color: #fff;
                            border-color: #4939B1;
                        }
                    }
                    .react-select__placeholder{
                        color: #fff;
                    }
                }
            }
        }
    }
    @media (max-width: ${SIZES.TABLET}) {
        .depth-list-wrap{
            padding: 24px 10px;

            &.fixed {
                top: 64px;
            }

            .menu{
                padding: 0;
                justify-content: flex-start;
                gap: 10px;
                .depth-prev, .depth-next{
                    width: 25px;
                    height: 25px;
                    svg{
                        width: 15px;
                        height: 15px;
                    }
                }
                .sub-list{
                    .sub-list-inner{
                        padding: 9px 16px;
                        font-weight: 600;
                        font-size: 14px;
                        line-height: 18px;
                    }
                }

                .sub-list-select {
                    .react-select-container {
                        .react-select__control{
                            padding: 9px 16px;
                            font-weight: 600;
                            font-size: 14px;
                            line-height: 18px;
                            height: auto;
                            min-width: auto;
                        }
                    }
                }
            }
        }
    }
`
export default PageDepthsInformation